/** Creating a new apollo client to use in the app
 *  defining a base route for the client
 *  enabling credentials
 *  sending xsrf token withthe requests
 *  configuring caching methods
 *  implementing middlewares
 */
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  from,
  InMemoryCache,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import * as Sentry from "@sentry/react";
import { Cookies } from "react-cookie";
import { v4 as uuidv4 } from "uuid";

const httpLink = createHttpLink({
  uri: import.meta.env.VITE_GQL,
  credentials: "include",
});

const reqMiddleware = new ApolloLink((operation, forward) => {
  // add the authorization to the headers
  const _h = { "X-CSRFToken": new Cookies().get("csrftoken") };
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      ..._h,
    },
  }));

  return forward(operation);
});

export const authenticationLink = onError(
  ({ graphQLErrors, networkError, operation }) => {
    const hasAuthenticationFailed =
      !!graphQLErrors && !!graphQLErrors.filter((e) => e.code === 401).length;

    // _af == Authentication Failed
    if (hasAuthenticationFailed) {
      localStorage.setItem("_af", 1);
    }
  }
);

export const sentryLink = onError(
  ({ graphQLErrors, networkError, operation }) => {
    const errorCode = `AP-${uuidv4().slice(0, 8).toUpperCase()}`;

    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, path, extensions }) => {
        Sentry.captureException(new Error(`[GraphQL error]: ${message}`), {
          tags: { errorCode },
          extra: {
            operation: operation.operationName,
            path,
            extensions,
          },
        });
      });
    }

    if (networkError) {
      Sentry.captureException(networkError, {
        tags: { errorCode },
      });
    }
  }
);

const link = from([reqMiddleware, authenticationLink, sentryLink, httpLink]);

const client = new ApolloClient({
  link,
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "no-cache",
    },
    query: {
      fetchPolicy: "no-cache",
    },
  },
});

export default client;
